import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  ReactNode,
} from "react";
import { authApi, ApiUser } from "@/services/api";
import { LOCAL_STORAGE_KEYS } from "@/config/constants";

/* ─── Tipos ──────────────────────────────────────────────────────────────── */

interface AuthState {
  user: ApiUser | null;
  loading: boolean;
  error: string | null;
}

interface AuthContextValue extends AuthState {
  login:  (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  isAuthenticated: boolean;
}

/* ─── Context ────────────────────────────────────────────────────────────── */

const AuthContext = createContext<AuthContextValue | null>(null);

/* ─── Provider ───────────────────────────────────────────────────────────── */

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user:    null,
    loading: true,
    error:   null,
  });

  const setUser = (user: ApiUser | null) => {
    setState((prev) => ({ ...prev, user, loading: false, error: null }));
    if (user) {
      localStorage.setItem(LOCAL_STORAGE_KEYS.USER, JSON.stringify(user));
    } else {
      localStorage.removeItem(LOCAL_STORAGE_KEYS.USER);
      localStorage.removeItem(LOCAL_STORAGE_KEYS.SESSION_TOKEN);
    }
  };

  const cancelInitialCheckRef = useRef<(() => void) | null>(null);

  /* Verifica sessão ativa ao montar */
  useEffect(() => {
    let cancelled = false;
    cancelInitialCheckRef.current = () => { cancelled = true; };

    const cached = localStorage.getItem(LOCAL_STORAGE_KEYS.USER);
    if (cached) {
      try {
        setState((prev) => ({ ...prev, user: JSON.parse(cached), loading: true }));
      } catch {
        localStorage.removeItem(LOCAL_STORAGE_KEYS.USER);
      }
    }

    authApi
      .me()
      .then(({ data }) => {
        if (cancelled) return;
        if (data.success && data.user) setUser(data.user);
        else setUser(null);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        // 401 = sessão inválida ou expirada → força re-login
        // Outros erros (rede, 5xx) → mantém o estado cacheado para não logar fora
        const status = (err as { response?: { status?: number } })?.response?.status;
        if (status === 401) {
          setUser(null);
        } else {
          setState((prev) => ({ ...prev, loading: false }));
        }
      });

    return () => { cancelled = true; };
  }, []);

  /* Escuta evento de 401 global (interceptor Axios) */
  useEffect(() => {
    const handle = () => setUser(null);
    window.addEventListener("semog:unauthorized", handle);
    return () => window.removeEventListener("semog:unauthorized", handle);
  }, []);

  const login = useCallback(async (username: string, password: string) => {
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const { data } = await authApi.login(username, password);
      if (data.success && data.user) {
        cancelInitialCheckRef.current?.();
        // Persiste o token de sessão para usar como fallback ao cookie
        if (data.sessionToken) {
          localStorage.setItem(LOCAL_STORAGE_KEYS.SESSION_TOKEN, data.sessionToken);
        }
        setUser(data.user);
      } else {
        setState((prev) => ({
          ...prev,
          loading: false,
          error: data.error ?? "Credenciais inválidas.",
        }));
      }
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ??
        "Erro ao conectar. Verifique sua conexão.";
      setState((prev) => ({ ...prev, loading: false, error: msg }));
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } finally {
      setUser(null);
    }
  }, []);

  return (
    <AuthContext.Provider
      value={{
        ...state,
        login,
        logout,
        isAuthenticated: !!state.user,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

/* ─── Hook ───────────────────────────────────────────────────────────────── */

export function useAuthContext(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuthContext deve ser usado dentro de <AuthProvider>");
  return ctx;
}
